'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import type { TicketedEventPublic, TicketedEventSchoolBranding } from '@school-journey/shared-types'
import { BRAND_SCOPE_ATTRIBUTE, brandStyleCss } from '../_lib/brand-style'
import { ctaLabel, formatEventDate } from '../_lib/event-presentation'
import { PublicSchoolHeader } from './public-school-header'
import { EventCard } from './event-card'

interface EventsGridClientProps {
  schoolSlug: string
  school: TicketedEventSchoolBranding
  /**
   * The hero, resolved SERVER-SIDE and handed to us (U8/KTD4). This component
   * no longer picks `events[0]`: the eligibility and fallback rules (R22–R24)
   * live next to the query that enforces them, so the hub and the collection
   * page — which both render this component — cannot drift apart.
   *
   * It is NOT present in `events`, so anything derived from "what is on this
   * page" must span both. Missing that is how a school with exactly one
   * eligible event ends up rendering a hero above "No upcoming events".
   */
  featured: TicketedEventPublic | null
  events: TicketedEventPublic[]
}

type FilterId = 'all' | 'this-week' | 'this-weekend' | 'free' | `kind:${string}`

const KIND_LABEL: Record<string, string> = {
  ticketed: 'Tickets',
  signup: 'Sign-ups',
  application: 'Applications'
}

function isThisWeek(d: Date, now: Date): boolean {
  return d >= now && d.getTime() <= now.getTime() + 7 * 86_400_000
}

function isThisWeekend(d: Date, now: Date): boolean {
  const daysUntilSat = (6 - now.getDay() + 7) % 7
  const sat = new Date(now)
  sat.setDate(now.getDate() + daysUntilSat)
  sat.setHours(0, 0, 0, 0)
  const sun = new Date(sat)
  sun.setDate(sat.getDate() + 1)
  sun.setHours(23, 59, 59, 999)
  return d >= sat && d <= sun
}

// "Free" is approximated by kind: signup/application never collect payment.
// Ticketed events may be free too, but the public list payload omits ticket
// tiers, so paid-vs-free is not derivable for them on the hub (documented
// limitation; revisit with a dedicated list-item schema).
function isFree(event: TicketedEventPublic): boolean {
  return event.kind !== 'ticketed'
}

/** A category offered by the strip: identity, label, and its (possibly 404ing) art. */
interface CategoryOption {
  id: string
  name: string
  imageUrl: string
  sortOrder: number
}

/**
 * The categories actually present on this page (U12/R4–R5).
 *
 * Derived from real event data, replacing the fixed decorative list that looked
 * tappable and did nothing. Options span the featured slot PLUS the list,
 * because R5 counts everything on the page and the hero is not in `events` —
 * otherwise the hero's category silently goes missing from its own page.
 *
 * A school with no categories assigned yields an empty array, and the strip
 * renders nothing at all (AE3) rather than ten dead circles.
 *
 * Exported for testing.
 */
export function categoriesPresent(events: TicketedEventPublic[]): CategoryOption[] {
  const byId = new Map<string, CategoryOption>()
  for (const event of events) {
    const c = event.category
    if (!c) continue
    if (!byId.has(c.id)) {
      byId.set(c.id, { id: c.id, name: c.name, imageUrl: c.imageUrl, sortOrder: c.sortOrder })
    }
  }
  // Curated order first (the seed's `sortOrder`), name as a stable tiebreak.
  return Array.from(byId.values()).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
}

export function EventsGridClient({ schoolSlug, school, featured, events }: EventsGridClientProps) {
  const [filter, setFilter] = useState<FilterId>('all')
  // Category selection is INDEPENDENT of the time/kind tabs and narrows
  // alongside them rather than replacing them: the two describe different
  // things (what an event is vs. when it is / how you respond), so combining
  // is what an admin filtering "Athletics" then "This week" expects.
  const [categoryId, setCategoryId] = useState<string | null>(null)

  // Everything on this page: the hero plus the grid. Every "what is present"
  // derivation below reads THIS, never `events` alone (KTD4).
  const onPage = useMemo(() => (featured ? [featured, ...events] : events), [featured, events])

  const kindsPresent = useMemo(() => {
    const seen = new Set<string>()
    for (const e of onPage) seen.add(e.kind)
    return Array.from(seen)
  }, [onPage])

  const categories = useMemo(() => categoriesPresent(onPage), [onPage])

  const visible = useMemo(() => {
    const now = new Date()
    return events.filter(e => {
      if (categoryId && e.category?.id !== categoryId) return false
      if (filter === 'all') return true
      if (filter === 'free') return isFree(e)
      if (filter === 'this-week') return isThisWeek(new Date(e.startsAt), now)
      if (filter === 'this-weekend') return isThisWeekend(new Date(e.startsAt), now)
      if (filter.startsWith('kind:')) return e.kind === filter.slice('kind:'.length)
      return true
    })
  }, [events, filter, categoryId])

  const hero = featured
  // Null for an un-branded school (or an unparseable token) — no style element
  // renders and the hub keeps the platform's default tokens.
  const brandCss = brandStyleCss(school.brand)

  return (
    <main className="bg-background text-foreground min-h-screen font-sans" {...{ [BRAND_SCOPE_ATTRIBUTE]: '' }}>
      {/* The school's own primary pair, for both theme modes — the filter tabs
          and category strip read `--primary`, so they follow the school's color
          rather than the platform default. */}
      {brandCss && <style>{brandCss}</style>}

      <PublicSchoolHeader school={school} />

      <div className="mx-auto max-w-[1280px] px-6">
        {hero && (
          <section className="mt-7">
            <Link
              href={`/events/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(hero.id)}`}
              className="relative block overflow-hidden rounded-[14px] bg-[#2a1a14]"
              style={{ aspectRatio: '1280 / 420' }}
            >
              {hero.coverImageUrl ? (
                <img src={hero.coverImageUrl} alt={hero.title} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-linear-to-br from-[#3d2c8d] to-[#1e0a3c]" />
              )}
              <div className="absolute inset-0 flex flex-col justify-center bg-linear-to-r from-[rgba(20,8,5,.78)] via-[rgba(20,8,5,.45)] to-transparent px-[7%]">
                <span className="mb-[14px] self-start rounded bg-[#f05537] px-[10px] py-[5px] text-[13px] font-extrabold uppercase tracking-[1.5px] text-white">
                  Featured
                </span>
                <h1 className="max-w-[720px] text-[clamp(30px,4.6vw,60px)] font-extrabold leading-[1.02] tracking-[-1px] text-white">
                  {hero.title}
                </h1>
                <div className="mt-[18px] flex flex-wrap gap-[18px] text-base font-semibold text-white/90">
                  <span>{formatEventDate(hero.startsAt, undefined, school.timezone)}</span>
                  {hero.locationText && (
                    <>
                      <span className="opacity-55">•</span>
                      <span>{hero.locationText}</span>
                    </>
                  )}
                </div>
                <span className="mt-6 self-start rounded-full bg-white px-[26px] py-[14px] text-base font-bold text-[#1e0a3c]">
                  {ctaLabel({ kind: hero.kind })}
                </span>
              </div>
            </Link>
          </section>
        )}

        {/* Category strip (U12) — real controls over real data. Rendered only
            when this page actually has categories to offer (R5/AE3). The strip
            scrolls WITHIN this container; the page itself never scrolls
            horizontally (R26). */}
        {categories.length > 0 && (
          <nav
            aria-label="Filter events by category"
            className="border-border flex gap-2 overflow-x-auto border-b px-3 pt-10 pb-9"
          >
            {categories.map(category => (
              <CategoryOptionButton
                key={category.id}
                category={category}
                active={categoryId === category.id}
                // Tapping the active category clears it — the strip is its own
                // toggle, so there is no separate "all categories" affordance
                // to go stale.
                onSelect={() => setCategoryId(prev => (prev === category.id ? null : category.id))}
              />
            ))}
          </nav>
        )}

        {/* Tabs — client-side filter state; kind tabs only for kinds present. */}
        <div className="border-border mt-9 flex gap-7 border-b" role="tablist">
          {(
            [
              ['all', 'All'],
              ['this-week', 'This week'],
              ['this-weekend', 'This weekend'],
              ['free', 'Free']
            ] as Array<[FilterId, string]>
          ).map(([id, label]) => (
            <FilterTab key={id} id={id} label={label} active={filter} onSelect={setFilter} />
          ))}
          {kindsPresent.map(kind => (
            <FilterTab
              key={kind}
              id={`kind:${kind}`}
              label={KIND_LABEL[kind] ?? kind}
              active={filter}
              onSelect={setFilter}
            />
          ))}
        </div>

        <div className="my-[26px] flex items-center gap-[10px]">
          <h2 className="text-2xl font-extrabold tracking-[-.3px]">Browse all events</h2>
        </div>

        {/* The empty state gates on the WHOLE page, not the grid: with the hero
            arriving separately, a school with exactly one eligible event has an
            empty `events` array and would otherwise render its hero above "No
            upcoming events". */}
        {onPage.length === 0 ? (
          <div className="text-muted-foreground pt-6 pb-20">
            <p className="text-foreground text-lg font-semibold">No upcoming events</p>
            <p className="mt-1 text-sm">Check back soon.</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-muted-foreground pt-6 pb-20">
            <p className="text-foreground text-lg font-semibold">
              {filter === 'all' ? 'No other events right now' : 'No events match this filter'}
            </p>
            <p className="mt-1 text-sm">
              {filter === 'all' && !categoryId
                ? 'The featured event above is the only one published.'
                : 'Try a different filter.'}
            </p>
          </div>
        ) : (
          <section className="grid grid-cols-1 gap-x-5 gap-y-6 pb-[60px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visible.map(event => (
              <EventCard key={event.id} schoolSlug={schoolSlug} event={event} schoolTimezone={school.timezone} />
            ))}
          </section>
        )}
      </div>
    </main>
  )
}

/**
 * One category option (U12/R8/R9).
 *
 * A real `<button>`, not the non-semantic `<div>` this strip used to render:
 * that is what makes it keyboard-operable and announced, and `aria-pressed`
 * is what tells assistive technology which category is currently selected.
 *
 * The artwork fallback is an ERROR handler, not a null check. `imageUrl` is
 * non-nullable and always populated, so the failure mode is a 404 at render
 * time — the seeded Cloudinary paths do not exist until the artwork is
 * commissioned (KTD7). On failure the option keeps its label and keeps
 * filtering; only the picture is replaced.
 */
function CategoryOptionButton({
  category,
  active,
  onSelect
}: {
  category: CategoryOption
  active: boolean
  onSelect: () => void
}) {
  const [artworkFailed, setArtworkFailed] = useState(false)

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      data-testid="category-option"
      className="focus-visible:outline-primary flex min-w-[78px] shrink-0 flex-col items-center gap-3 rounded-lg px-1 py-1 focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <span
        className={
          'text-foreground/85 grid h-[76px] w-[76px] place-items-center overflow-hidden rounded-full border ' +
          (active ? 'border-primary ring-primary ring-2' : 'border-border')
        }
      >
        {artworkFailed ? (
          // Decorative stand-in — the label below already names the category,
          // so this must not be announced a second time.
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            aria-hidden="true"
            data-testid="category-artwork-fallback"
          >
            <circle cx="12" cy="12" r="8.5" />
          </svg>
        ) : (
          <img
            src={category.imageUrl}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            onError={() => setArtworkFailed(true)}
          />
        )}
      </span>
      <span className={'text-center text-[13px] font-semibold ' + (active ? 'text-primary' : 'text-foreground/85')}>
        {category.name}
      </span>
    </button>
  )
}

function FilterTab({
  id,
  label,
  active,
  onSelect
}: {
  id: FilterId
  label: string
  active: FilterId
  onSelect: (id: FilterId) => void
}) {
  const isActive = active === id
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => onSelect(id)}
      className={
        '-mb-px border-b-2 px-[2px] py-3 text-[15px] font-semibold ' +
        (isActive ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground border-transparent')
      }
    >
      {label}
    </button>
  )
}
